import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, AlertTriangle, ClipboardList } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import ProductCatalogTab from '@/components/supplements/ProductCatalogTab';
import StackBuilderTab from '@/components/supplements/StackBuilderTab';
import AssignmentTab from '@/components/supplements/AssignmentTab';
import DeliveryTab from '@/components/supplements/DeliveryTab';
import InventoryTab from '@/components/supplements/InventoryTab';
import AnalyticsTab from '@/components/supplements/AnalyticsTab';

export default function SupplementTracker() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';
  const isTeamLeader = user.role === 'team_leader';
  const isOpsTeam = user.role === 'ops_team';

  const [activeTab, setActiveTab] = useState(isOpsTeam ? 'delivery' : 'catalog');
  const [summaryStats, setSummaryStats] = useState(null);

  // Tab definitions with role visibility
  const tabs = [
    { id: 'catalog', label: 'Catalog', roles: ['admin', 'ops_team'] },
    { id: 'stacks', label: 'Stacks', roles: ['admin', 'ops_team'] },
    { id: 'assignments', label: 'Assignments', roles: ['admin', 'manager', 'team_leader', 'ops_team'] },
    { id: 'delivery', label: 'Delivery', roles: ['admin', 'manager', 'ops_team'] },
    { id: 'inventory', label: 'Inventory', roles: ['admin', 'ops_team'] },
    { id: 'analytics', label: 'Analytics', roles: ['admin', 'manager', 'ops_team'] },
  ];

  const visibleTabs = tabs.filter(tab => tab.roles.includes(user.role));

  // If current active tab is not visible, default to first visible
  const effectiveTab = visibleTabs.find(t => t.id === activeTab)
    ? activeTab
    : visibleTabs[0]?.id || 'catalog';

  // Lazy-fetch summary stats for the header bar
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const [analyticsRes, alertsRes] = await Promise.allSettled([
          axiosInstance.get('/supplements/analytics'),
          axiosInstance.get('/supplements/inventory/alerts', { params: { threshold: 10 } }),
        ]);
        const analytics = analyticsRes.status === 'fulfilled' ? analyticsRes.value.data : {};
        const alerts = alertsRes.status === 'fulfilled' ? alertsRes.value.data : {};
        const funnel = analytics.funnel || analytics;
        const activeAssignments = (funnel.total_assigned || 0) - (funnel.total_customer_received || 0) - (funnel.total_cancelled || 0);
        const overdue = analytics.sla?.overdue_count || analytics.overdue_count || (analytics.escalations || []).length || 0;
        const lowStock = (alerts.low_stock || []).length;
        setSummaryStats({
          active: Math.max(0, activeAssignments),
          overdue,
          lowStock,
        });
      } catch {
        // non-critical
      }
    };
    if (isAdmin || isManager || isOpsTeam) fetchSummary();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTabContent = () => {
    switch (effectiveTab) {
      case 'catalog':
        return <ProductCatalogTab isAdmin={isAdmin || isOpsTeam} />;
      case 'stacks':
        return <StackBuilderTab isAdmin={isAdmin || isOpsTeam} />;
      case 'assignments':
        return <AssignmentTab user={user} />;
      case 'delivery':
        return <DeliveryTab user={user} />;
      case 'inventory':
        return <InventoryTab isAdmin={isAdmin || isOpsTeam} />;
      case 'analytics':
        return <AnalyticsTab user={user} />;
      default:
        return null;
    }
  };

  return (
    <div data-testid="supplement-tracker-page">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-primary" />
          <div>
            <h1 className="page-title">SUPPLEMENT TRACKER</h1>
            <p className="page-subtitle">Manage supplement stacks, assignments, and deliveries</p>
          </div>
        </div>
      </div>

      {/* Summary Stats Bar */}
      {summaryStats && (isAdmin || isManager || isOpsTeam) && (
        <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
            <ClipboardList className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800 font-medium">{summaryStats.active} Active Assignments</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${summaryStats.overdue > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <AlertTriangle className={`w-4 h-4 ${summaryStats.overdue > 0 ? 'text-red-600' : 'text-green-600'}`} />
            <span className={`font-medium ${summaryStats.overdue > 0 ? 'text-red-800' : 'text-green-800'}`}>
              {summaryStats.overdue} Overdue
            </span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${summaryStats.lowStock > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
            <Package className={`w-4 h-4 ${summaryStats.lowStock > 0 ? 'text-orange-600' : 'text-green-600'}`} />
            <span className={`font-medium ${summaryStats.lowStock > 0 ? 'text-orange-800' : 'text-green-800'}`}>
              {summaryStats.lowStock} Low Stock Products
            </span>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <Tabs value={effectiveTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          {visibleTabs.map(tab => (
            <TabsTrigger key={tab.id} value={tab.id} data-testid={`tab-${tab.id}`}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
}
